export const ORDER_MODES = [
  { value: "custom", label: "Order: my order" },
  { value: "budget", label: "Order: highest budget" },
  { value: "newest", label: "Order: newest" },
  { value: "agents", label: "Order: same as your agents" },
];

const budgetOf = (item) => Number(item.budgetMax) || Number(item.budgetMin) || 0;
const timeOf = (item) => new Date(item.createdAt || 0).getTime() || 0;

export const normalize = (items) => [...items.filter((item) => item.pinned), ...items.filter((item) => !item.pinned)];

export const sortRest = (rest, mode) => {
  if (mode === "budget") return [...rest].sort((a, b) => budgetOf(b) - budgetOf(a));
  if (mode === "newest") return [...rest].sort((a, b) => timeOf(b) - timeOf(a));
  return rest;
};

export const matchesQuery = (item, query) => {
  const needle = String(query || "").trim().toLowerCase();
  if (!needle) return true;
  return [item.title, item.location, item.agentName].filter(Boolean).join(" ").toLowerCase().includes(needle);
};

export const groupsOf = (items, mode, query) => {
  if (mode === "agents") return { pinned: [], rest: items.filter((item) => matchesQuery(item, query)) };
  const pinned = items.filter((item) => item.pinned);
  const rest = sortRest(
    items.filter((item) => !item.pinned),
    mode,
  );
  return {
    pinned: pinned.filter((item) => matchesQuery(item, query)),
    rest: rest.filter((item) => matchesQuery(item, query)),
  };
};

const settle = (items, mode) => {
  const pinned = items.filter((item) => item.pinned);
  return [...pinned, ...sortRest(items.filter((item) => !item.pinned), mode)];
};

export const moveItem = (items, wantAdId, step, mode, query) => {
  const current = items.find((item) => item.wantAdId === wantAdId);
  if (!current) return null;
  const settled = settle(items, mode);
  const groups = groupsOf(settled, "custom", query);
  const group = current.pinned ? groups.pinned : groups.rest;
  const index = group.findIndex((item) => item.wantAdId === wantAdId);
  const neighbor = group[index + step];
  if (index < 0 || !neighbor) return null;
  const without = settled.filter((item) => item.wantAdId !== wantAdId);
  const at = without.findIndex((item) => item.wantAdId === neighbor.wantAdId);
  without.splice(step > 0 ? at + 1 : at, 0, current);
  return normalize(without);
};

export const dropItem = (items, dragId, targetId, mode) => {
  if (dragId === targetId) return null;
  const settled = settle(items, mode);
  const dragged = settled.find((item) => item.wantAdId === dragId);
  const target = settled.find((item) => item.wantAdId === targetId);
  if (!dragged || !target) return null;
  const from = settled.indexOf(dragged);
  const to = settled.indexOf(target);
  const without = settled.filter((item) => item.wantAdId !== dragId);
  const at = without.indexOf(target);
  const sameGroup = dragged.pinned === target.pinned;
  without.splice(sameGroup && from < to ? at + 1 : at, 0, { ...dragged, pinned: target.pinned });
  return normalize(without);
};

export const dropAtEnd = (items, dragId, pinned, mode) => {
  const settled = settle(items, mode);
  const dragged = settled.find((item) => item.wantAdId === dragId);
  if (!dragged) return null;
  const without = settled.filter((item) => item.wantAdId !== dragId);
  return normalize([...without, { ...dragged, pinned }]);
};

export const togglePin = (items, wantAdId) => {
  const ordered = normalize(items);
  const current = ordered.find((item) => item.wantAdId === wantAdId);
  if (!current) return null;
  const without = ordered.filter((item) => item.wantAdId !== wantAdId);
  const next = { ...current, pinned: !current.pinned };
  const lastPinned = without.filter((item) => item.pinned).length;
  without.splice(lastPinned, 0, next);
  return normalize(without);
};

export const toggleHidden = (items, wantAdId) =>
  items.map((item) => (item.wantAdId === wantAdId ? { ...item, hidden: !item.hidden } : item));

export const payloadOf = (items) =>
  normalize(items).map(({ wantAdId, pinned, hidden }) => ({ wantAdId, pinned: Boolean(pinned), hidden: Boolean(hidden) }));
